// Reactive, IndexedDB-backed contact store. Each contact is its own record in
// the 'contacts' object store (keyed by id), so getAll() returns the book. An
// in-memory snapshot + pub/sub drives the UI via useSyncExternalStore, mirroring
// the session store's identity contract: every mutation produces a NEW array so
// subscribers re-render. The IndexedDB write is fire-and-forget after the cache
// updates, keeping the UI synchronous-feeling.

import { useSyncExternalStore } from 'react'
import { openDb } from '@/lib/idb/idb'
import type { Contact } from './types'

let cache: Contact[] = []
let hydrated = false
let hydrating: Promise<void> | null = null
const listeners = new Set<() => void>()

function emit(): void {
  for (const l of listeners) l()
}

function sortByName(list: Contact[]): Contact[] {
  return [...list].sort((a, b) => a.name.localeCompare(b.name))
}

async function hydrate(): Promise<void> {
  if (hydrated) return
  if (!hydrating) {
    hydrating = (async () => {
      const db = await openDb()
      cache = sortByName(await db.getAll<Contact>('contacts'))
      hydrated = true
      emit()
    })()
  }
  await hydrating
}

/** Kick off the one-time load (idempotent). Snapshot updates when it lands. */
export function ensureContactsLoaded(): void {
  void hydrate()
}

function getSnapshot(): Contact[] {
  return cache
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  ensureContactsLoaded()
  return () => listeners.delete(listener)
}

/** Create or update a contact, persisting it. */
export async function upsertContact(contact: Contact): Promise<void> {
  await hydrate()
  const rest = cache.filter((c) => c.id !== contact.id)
  cache = sortByName([...rest, contact])
  emit()
  const db = await openDb()
  await db.put('contacts', contact, contact.id)
}

/** Delete a contact by id. */
export async function deleteContact(id: string): Promise<void> {
  await hydrate()
  cache = cache.filter((c) => c.id !== id)
  emit()
  const db = await openDb()
  await db.delete('contacts', id)
}

/** Look a contact up in the current snapshot (synchronous, best-effort). */
export function getContact(id: string | null | undefined): Contact | undefined {
  if (!id) return undefined
  return cache.find((c) => c.id === id)
}

/** React binding — the live contact list, hydrated from IndexedDB on first use. */
export function useContacts(): Contact[] {
  return useSyncExternalStore(subscribe, getSnapshot)
}
